const incomingRawMaterialCertification = (row: any): "Y" | "N" | "" => {
  const explicit = String(row?.isCertified ?? row?.is_certified ?? "")
    .trim()
    .toUpperCase();
  if (explicit === "Y" || explicit === "N") return explicit;
  return row?.tcProductIncomingCertifiedRawMaterialCode ? "Y" : "";
};

export const isCertifiedIncomingRawMaterial = (row: any): boolean =>
  incomingRawMaterialCertification(row) === "Y";

export const filterIncomingRawMaterialsForSource = <T,>(
  rows: T[] | null | undefined,
  _sourceType: any
): T[] => {
  // Every source RM remains available to Steps 2 and 3 for reference.
  return Array.isArray(rows) ? rows : [];
};

export const filterCertifiedIncomingRawMaterials = <T,>(
  rows: T[] | null | undefined
): T[] => {
  const values = Array.isArray(rows) ? rows : [];
  return values.filter((row) => isCertifiedIncomingRawMaterial(row));
};

export const resolveIncomingRawMaterialWeightValue = (
  enteredWeights: Record<string, string | number | null | undefined> | null | undefined,
  rawMaterialKey: string,
  fallbackValue: string | number
): string | number => enteredWeights?.[rawMaterialKey] ?? fallbackValue;

export const withIncomingRawMaterialKeys = <T extends Record<string, any>>(
  rows: T[] | null | undefined
): Array<T & { rawMaterialKey: string }> => {
  const values = Array.isArray(rows) ? rows : [];
  const occurrences = new Map<string, number>();
  return values.map((row) => {
    const base = [
      String(row.tcProductCategoryCode || "").trim().toUpperCase(),
      String(row.tcProductDetailCode || "").trim().toUpperCase(),
      String(
        row.rawMaterialCode ||
        row.tcProductIncomingCertifiedRawMaterialCode ||
        ""
      ).trim().toUpperCase(),
      Number(row.rawMaterialPercentage || 0),
      String(row.isCertified ?? row.is_certified ?? "").trim().toUpperCase(),
    ].join("|");
    const occurrence = occurrences.get(base) || 0;
    occurrences.set(base, occurrence + 1);
    return {
      ...row,
      rawMaterialKey: row.rawMaterialKey || `${base}|${occurrence}`,
    };
  });
};

const identityPart = (value: any): string =>
  String(value ?? "").trim().toUpperCase();

export const incomingRawMaterialIdentity = (row: any): string => {
  const rawMaterialCode = identityPart(
    row?.rawMaterialCode ?? row?.tcProductIncomingCertifiedRawMaterialCode
  );
  if (!rawMaterialCode) return "";
  return [
    identityPart(row?.tcProductCategoryCode),
    identityPart(row?.tcProductDetailCode),
    rawMaterialCode,
  ].join("|");
};

export const mergeIncomingRawMaterialsForDisplay = (
  sourceRows: any[] | null | undefined,
  storedRows: any[] | null | undefined
): any[] => {
  const source = withIncomingRawMaterialKeys(
    Array.isArray(sourceRows) ? sourceRows : []
  );
  const stored = Array.isArray(storedRows) ? storedRows : [];

  if (source.length === 0) {
    return withIncomingRawMaterialKeys(
      stored.map((row) => ({
        ...row,
        isCertified:
          row?.isCertified ??
          row?.is_certified ??
          (row?.tcProductIncomingCertifiedRawMaterialCode ? "Y" : ""),
      }))
    );
  }

  const matchedStoredIndexes = new Set<number>();
  return source.map((sourceRow) => {
    const identity = incomingRawMaterialIdentity(sourceRow);
    const storedIndex = identity
      ? stored.findIndex((storedRow, index) => {
          const sourceCertification = incomingRawMaterialCertification(sourceRow);
          const storedCertification = incomingRawMaterialCertification(storedRow);
          const certificationMatches = sourceCertification || storedCertification
            ? sourceCertification === storedCertification
            : true;
          return (
            !matchedStoredIndexes.has(index) &&
            incomingRawMaterialIdentity(storedRow) === identity &&
            certificationMatches
          );
        })
      : -1;
    const storedRow = storedIndex >= 0 ? stored[storedIndex] : undefined;
    if (storedIndex >= 0) matchedStoredIndexes.add(storedIndex);

    const storedWeight =
      storedRow?.rawMaterialWeight ??
      storedRow?.raw_material_weight ??
      storedRow?.tcProductIncomingCertifiedRawMaterialWeightUsed;
    const merged: any = {
      ...sourceRow,
      isCertified:
        sourceRow?.isCertified ??
        sourceRow?.is_certified ??
        storedRow?.isCertified ??
        storedRow?.is_certified ??
        (storedRow?.tcProductIncomingCertifiedRawMaterialCode ? "Y" : ""),
    };

    if (storedWeight !== undefined && storedWeight !== null && storedWeight !== "") {
      merged.rawMaterialWeight = Number(storedWeight) || 0;
    }
    return merged;
  });
};
